
import React, {useState, useEffect, useRef, forwardRef, ReactElement} from 'react';
import { DndProvider } from 'react-dnd';
// import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Menu, Item, useContextMenu } from 'react-contexify';
import classNames from 'classnames';
import { Eraser } from 'lucide-react';
import { getTextLengthUpToNode } from '@/utils/dom';
import {getNodesInRange, findClosestAncestorWithKey, isSelectionInElement} from '@/utils/range';
import { canAddHighlight, normalizeHighlightPositions } from '@/utils/highlight';
import { generateUuid } from '@/utils/uuid';
import { IClearHighlightPayload, IGenericObj, IPageDataBoxType, IPosItem, IhightLightObj, IhightLightItem } from '@/utils/ctype';
import { getZoom } from '@/utils/scaleWindow';
import { CustomDragLayer } from '../Preview';
import TopBar from '../TopBar';
import PartTitle from '../PartTitle';
import style from './index.module.less';
import 'react-contexify/ReactContexify.css';
import './index.less';

const SELECTION_MENU_ID = 'selectionActions';
const HIGHLIGHT_MENU_ID = 'highlightActions';
const SELECTION_MENU_WIDTH = 145;
const SELECTION_MENU_HEIGHT = 66;
const SELECTION_MENU_GAP = 1;

function NotesMenuIcon() {
  return (
    <svg className="selectionMenuIcon" viewBox="0 0 20 20" aria-hidden>
      <path fill="currentColor" d="M2 2.5h16v13h-4.8L10 18v-2.5H2z" />
      <path fill="#fff" d="M5.6 6.7h2.6v2.6H7c0 .9.4 1.5 1.2 1.9l-.8 1.3c-1.3-.6-1.9-1.6-1.9-3.2V6.7zm5 0h2.6v2.6H12c0 .9.4 1.5 1.2 1.9l-.8 1.3c-1.3-.6-1.9-1.6-1.9-3.2V6.7z" />
    </svg>
  );
}

function HighlightMenuIcon() {
  return (
    <svg className="selectionMenuIcon" viewBox="0 0 20 20" aria-hidden>
      <path fill="currentColor" d="M12 1.5h5v11.2l-2.5 2.6-2.5-2.6z" />
      <path fill="currentColor" d="M2.5 16.5h15v2h-15z" />
    </svg>
  );
}

interface IPendingSelection {
  posArr: IPosItem[];
}

interface IPendingHighlightAction {
  posArr: IPosItem[];
  isNote: boolean;
}

function clearSelection() {
  console.log('clearSelection');
  if ((window as any).getSelection) {
    // For modern browsers
    const selection = (window as any).getSelection();
    if (selection.rangeCount > 0) {
      selection.removeAllRanges();
    }
  } else if ((document as any).selection) {
    // For older versions of IE
    (document as any).selection.empty();
  }
}

interface IProp {
  children: React.ReactNode;
  headerChildren?: React.ReactNode;
  pageData: IPageDataBoxType | undefined;
  loadAnswered: boolean;
  highLightObj: IhightLightObj;
  isAI?: boolean;
  time: number;
  saveHighlightDataHandle: (val: {show: boolean;wrapUid: string;} & IhightLightItem) => void;
  tabSubjectHandle?: (val: boolean) => void;
  isSubject?: boolean;
  isListen?: boolean;
  originalText?: ReactElement;
  updateTimeHandle: (val: number)=> void;
  submitHandle: (callback: () => void) => void | Promise<boolean>;
  clearDialogHandle: (payload?: IClearHighlightPayload)=> void;
  noteHandle: () => void;
  showNotePanel: boolean;
  notePanel?: React.ReactNode;
  aiContent?: React.ReactNode;
  course: 'r' | 'l' | 'w';
  draftSyncedAt?: number;
}


export default forwardRef(function Main(props: IProp, ref) {

  const {children, pageData, loadAnswered, highLightObj = {}, isAI, time, saveHighlightDataHandle, tabSubjectHandle, isSubject, isListen, originalText,
    updateTimeHandle, submitHandle, clearDialogHandle, noteHandle, showNotePanel, notePanel, aiContent, headerChildren, course, draftSyncedAt} = props;


  const [disableInfo, setDisableInfo] = useState({disableHighlight: true, disableNotes: true});

  const pendingSelection = useRef<IPendingSelection | null>(null);
  const pendingHighlightAction = useRef<IPendingHighlightAction | null>(null);
  const showMenuTimer = useRef<number>();


  const highlightHandle = (needNote: boolean) => {
    try {
      const selectionData = pendingSelection.current;
      if (!selectionData || !canAddHighlight(highLightObj, selectionData.posArr, needNote).allowed) {
        return;
      }

      const uid = generateUuid();
      const posArr = selectionData.posArr.map((item) => ({...item, uid, hasPanel: needNote}));
      const posObj = posArr.reduce<IGenericObj<IPosItem[]>>((all, item) => {
        if (!all[item.dataKey]) {
          all[item.dataKey] = [];
        }
        all[item.dataKey].push(item);
        return all;
      }, {});

      saveHighlightDataHandle({
        uid,
        posObj,
        posArr,
        panelData: null,
        show: needNote,
        wrapUid: '',
        createdAt: Date.now(),
      });

      pendingSelection.current = null;
      clearSelection();
      if (needNote && !showNotePanel) {
        noteHandle();
      }
    } catch (err) {
      console.error(err);
    }
    
  };

  const deleteHighlightHandle = () => {
    const highlightAction = pendingHighlightAction.current;
    if (!highlightAction) {
      return;
    }
    if (highlightAction.isNote) {
      if (!showNotePanel) {
        noteHandle();
      }
    } else {
      clearDialogHandle({posArr: highlightAction.posArr});
    }
    pendingHighlightAction.current = null;
  };

  const {show: showSelectionMenu} = useContextMenu({
    id: SELECTION_MENU_ID,
  });
  const {show: showHighlightMenu} = useContextMenu({
    id: HIGHLIGHT_MENU_ID,
  });



  const { title, desc } = pageData || {};

  const createPendingSelection = (range: Range): IPendingSelection | null => {
    const rawPositions = getNodesInRange(range).reduce<IPosItem[]>((all, item) => {
      const {node, startOffset, endOffset} = item;
      const closestAncestor = findClosestAncestorWithKey(node);
      const dataKey = closestAncestor?.dataset.key;
      if (!closestAncestor || !dataKey || endOffset <= startOffset) {
        return all;
      }
      const {dataLength} = getTextLengthUpToNode(closestAncestor, node);
      all.push({
        dataKey,
        startOffset: dataLength + startOffset,
        endOffset: dataLength + endOffset,
        uid: '',
        hasPanel: false,
      });
      return all;
    }, []);
    const posArr = normalizeHighlightPositions(rawPositions);
    if (!posArr.length) {
      return null;
    }
    return {posArr};
  };

  const handleSelectionMouseUp = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) {
      return;
    }
    const selection = window.getSelection();
    const highlightContent = document.getElementById('highlightContent');
    if (!selection || !highlightContent || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (!isSelectionInElement(range, highlightContent)) {
      return;
    }
    const selectionData = createPendingSelection(range);
    if (!selectionData) {
      return;
    }

    pendingSelection.current = selectionData;
    pendingHighlightAction.current = null;

    setDisableInfo({
      disableHighlight: !canAddHighlight(highLightObj, selectionData.posArr, false).allowed,
      disableNotes: !canAddHighlight(highLightObj, selectionData.posArr, true).allowed,
    });

    const zoom = getZoom();
    const rect = range.getBoundingClientRect();
    const viewportWidth = window.innerWidth / zoom;
    const rectLeft = rect.width ? rect.left / zoom : event.clientX / zoom;
    const rectTop = rect.height ? rect.top / zoom : event.clientY / zoom;
    const rectBottom = rect.height ? rect.bottom / zoom : event.clientY / zoom;
    const viewportHeight = window.innerHeight / zoom;
    const x = Math.max(8, Math.min(viewportWidth - SELECTION_MENU_WIDTH - 8, rectLeft));
    const belowY = rectBottom + SELECTION_MENU_GAP;
    const y = belowY + SELECTION_MENU_HEIGHT <= viewportHeight - 8
      ? belowY
      : Math.max(8, rectTop - SELECTION_MENU_HEIGHT - SELECTION_MENU_GAP);
    const eventPosition = {x, y};

    const triggerEvent = {
      clientX: event.clientX,
      clientY: event.clientY,
      preventDefault: () => undefined,
      stopPropagation: () => undefined,
    } as unknown as React.MouseEvent<HTMLDivElement>;
    window.clearTimeout(showMenuTimer.current);
    showMenuTimer.current = window.setTimeout(() => {
      showSelectionMenu({event: triggerEvent, props: {source: 'selection'}, position: eventPosition});
    }, 0);
  };

  const handleHighlightClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const highlight = (event.target as HTMLElement).closest?.('.highLight') as HTMLElement | null;
    if (!highlight || !event.currentTarget.contains(highlight)) {
      return;
    }
    if (highlight.classList.contains('highLightNote')) {
      pendingSelection.current = null;
      pendingHighlightAction.current = null;
      window.clearTimeout(showMenuTimer.current);
      clearSelection();
      if (!showNotePanel) {
        noteHandle();
      }
      return;
    }

    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      return;
    }
    const range = document.createRange();
    range.selectNodeContents(highlight);
    const selectionData = createPendingSelection(range);
    if (!selectionData) {
      return;
    }

    pendingSelection.current = null;
    pendingHighlightAction.current = {
      posArr: selectionData.posArr,
      isNote: false,
    };
    const zoom = getZoom();
    showHighlightMenu({
      event,
      props: {source: 'highlight'},
      position: {x: event.clientX / zoom, y: event.clientY / zoom},
    });
  };

  const handleItemClick = ({id} :{id: string;} ) => {
    switch (id) {
      case 'Highlight':
        highlightHandle(false);
        break;
      case 'Notes':
        highlightHandle(true);
        break;
    }
  };

  useEffect(() => {
    return () => {
      window.clearTimeout(showMenuTimer.current);
    };
  }, []);


  if (!pageData) {
    return null;
  }

  const menuMousedownHandle = (eve: React.MouseEvent) => {
    eve.preventDefault();
  };
  const {disableHighlight, disableNotes} = disableInfo;
  return (
    <div className={style.mainContent}>
      
      <TopBar course={course} audioSrc={pageData?.question.file_url} time={time} isAI={isAI|| false}
        tabSubjectHandle={tabSubjectHandle || (() => undefined)} isSubject={isSubject || false} isListen={isListen || false}
        loadAnswered={loadAnswered} updateTimeHandle={updateTimeHandle} submitHandle={submitHandle} draftSyncedAt={draftSyncedAt}
        noteHandle={noteHandle} ></TopBar>
      <div className={style.aiWrap}>
        <div className={style.contentArea}>
          <div className={style.mainWrap}>
            <DndProvider backend={TouchBackend} options={{ enableMouseEvents: true }}>
              <CustomDragLayer />
              <div className={classNames(style.wrap, isAI ? style.isAI : '')} id="highlightContent" onMouseUp={handleSelectionMouseUp} onClick={handleHighlightClick} style={{display: !isListen ||isSubject ? 'flex' : 'none'}}>
                {isListen ? null : <PartTitle title={title||''} desc={desc|| ''}>{headerChildren}</PartTitle>}
                {children}
              </div>
            </DndProvider>
            <div className={`${style.textWrap}`} style={{display: !isListen ||isSubject ? 'none' : 'block'}}>
              {originalText}
            </div>
          </div>
          {
            isAI && aiContent ? <div className={style.aiPanel}>{aiContent}</div> : null
          }
        </div>
        {notePanel}
      </div>
      <Menu id={SELECTION_MENU_ID} className="selectionMenu" animation="fade" onMouseDown={menuMousedownHandle}>
        <Item id="Notes" className="selectionMenuItem" onClick={(val: any) => handleItemClick(val)} disabled={disableNotes}>
          <NotesMenuIcon />
          <span>Notes</span>
        </Item>
        <Item id="Highlight" className="selectionMenuItem" onClick={(val: any) => handleItemClick(val)} disabled={disableHighlight}>
          <HighlightMenuIcon />
          <span>Highlight</span>
        </Item>
      </Menu>
      <Menu id={HIGHLIGHT_MENU_ID} className="selectionMenu highlightDeleteMenu" animation="fade" onMouseDown={menuMousedownHandle}>
        <Item id="DeleteHighlight" className="selectionMenuItem" onClick={deleteHighlightHandle}>
          <Eraser className="selectionMenuIcon" aria-hidden strokeWidth={1.8} />
          <span>Remove highlight</span>
        </Item>
      </Menu>
      
    </div>
  );
});
